import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { RecaptchaComponent } from 'src/app/shared/components/molecules/recaptcha/recaptcha.component';
import { Subscription } from 'rxjs';
import { AuthenticationService } from 'src/app/core/services/authentication/authentication.service';
import { NavigationService } from 'src/app/core/services/navigation/navigation.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { LimitExceededException } from 'src/app/shared/errors/cognito/cognito-errors';
import { RoutesKeys } from 'src/app/utils/routes';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { Routes } from 'src/app/account-migration/routes';
import { environment } from '../../../environments/environment';

@Component({
  templateUrl: './forgot-password.page.html',
})
/**
 * Instantiates change password template with forgot password elements
 */
export class ForgotPasswordPage implements OnInit, OnDestroy {
  formGroup!: FormGroup<{
    email: FormControl<string | null | undefined>;
    recaptcha: FormControl;
  }>;
  errorMessage = '';
  loading = false;
  private subsink = new Subscription();
  @ViewChild('captchaRef') captchaRef?: RecaptchaComponent;
  readonly labels = {
    verifyYourEmailTitle: 'verify-your-email-dialog-title',
    verifyYourEmailContent: 'verify-your-email-dialog-content',
    successButtonText: 'ok',
  };
  recaptchaEnabled = environment.recaptcha.enabled;

  /**
   * @constructor
   */
  constructor(
    private translationService: TranslationService,
    private navigationService: NavigationService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private readonly dialogService: DialogService
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    this.formGroup = new FormGroup({
      email: new FormControl(this.authenticationService.passwordResetEmail, [
        Validators.email,
        Validators.required,
      ]),
      recaptcha: new FormControl(
        '',
        this.recaptchaEnabled ? Validators.required : []
      ),
    });
    this.subsink.add(
      this.formGroup.get('email')?.valueChanges.subscribe((email) => {
        this.errorMessage = '';
        this.authenticationService.passwordResetEmail = email || undefined;
      })
    );
  }

  /**
   * Cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Return to previous page in history
   */
  goBack() {
    this.navigationService.back();
  }

  /**
   * Tells service that a password was forgotten
   */
  async forgotPassword() {
    this.loading = true;
    const { email, recaptcha } = this.formGroup.getRawValue();
    const recaptchaToken = this.recaptchaEnabled ? recaptcha : 'dev-bypass';
    if (!email) {
      return;
    }
    (
      await this.authenticationService.resetPassword(email, recaptchaToken)
    ).subscribe({
      next: (res: any) => {
        if (res.isMigrationTemporaryReset) {
          this.router.navigate([
            `${RoutesKeys.ACCOUNT_MIGRATION}/${Routes.LOGIN}`,
          ]);
        } else {
          this.router.navigate([RoutesKeys.CREATE_NEW_PASSWORD]);
        }
      },
      error: (error) => {
        this.handleError(error);
      },
      complete: () => {
        this.loading = false;
        if (this.recaptchaEnabled) {
          this.captchaRef?.reset();
        }
      },
    });
  }

  /**
   * Decides if forgot password button should be disabled
   * @return {boolean}
   */
  isForgotPasswordDisabled(): boolean {
    return this.formGroup.invalid || this.loading;
  }

  /**
   * Returns email form control
   */
  get emailControl() {
    return this.formGroup.controls['email'];
  }

  /**
   * Handles errors that might happen with forgot password
   * @param {Error} error
   */
  private async handleError(error: Error) {
    let errorMessage = 'error-recovering-password';
    if (error instanceof LimitExceededException) {
      errorMessage = 'error-forgot-password-limit-exceeded';
    }

    if (error.message?.startsWith('User not confirmed')) {
      this.errorMessage = error.message;
      this.openVerifyEmailDialog();
      return;
    }

    this.errorMessage = await this.translationService.translateString(
      errorMessage
    );
  }

  /**
   * Shows dialog to inform the user that the e-mail used to recover the password is still not verified.
   */
  private async openVerifyEmailDialog() {
    const verifyEmailData = {
      title: this.labels.verifyYourEmailTitle,
      content: this.labels.verifyYourEmailContent,
      successButtonText: this.labels.successButtonText,
    };

    await this.translationService.translateObject(verifyEmailData);

    this.dialogService.openDialog(
      verifyEmailData,
      () => {
        this.navigateToEmailVerificationPage();
      },
      () => {
        this.navigateToEmailVerificationPage();
      },
      600
    );
  }

  /**
   * Navigates to email verification page.
   */
  private navigateToEmailVerificationPage(): void {
    this.router.navigate([RoutesKeys.VERIFICATION]);
  }
}
